import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { AnalyticsService } from '@core/services/analytics.service';
import { DateService } from '@core/services/date.service';
import { DeviceService } from '@core/services/device.service';
import { HotelsResultsService } from '@core/services/hotel-results.service';
import { I18nService } from '@core/services/i18n.service';
import { ReqService } from '@core/services/req.service';
import { SearchStateService } from '@core/services/search-state.service';
import { IHotelEntityCity } from '@core/models/hotel';
import { MbBasicDialog, MbDialog, MbDialogRef, MbButton, MbDialogHeader, MbDialogBody } from '@am/moebius';
import { SearchBoxComponent } from '@am/search-box';
import { SearchStatusComponent } from '../search-status/search-status.component';
import { NgClass } from '@angular/common';

@Component({
    selector: 'search-widget',
    templateUrl: './search-widget.component.html',
    styleUrls: ['./search-widget.component.scss'],
    standalone: true,
    imports: [
        MbButton,
        MbBasicDialog,
        SearchStatusComponent,
        SearchBoxComponent,
        NgClass,
        MbDialogHeader,
        MbDialogBody,
    ],
})
export class SearchWidgetComponent implements OnInit, OnChanges {
  @Input()
  searchedEntity: any;
  @Input()
  busy: boolean;
  @Input()
  section: string;
  @Output()
  resetState: EventEmitter<any> = new EventEmitter<any>();
  @Input()
  isDisney: boolean;
  @Input()
  total: number;
  @Input()
  isLanding: boolean;
  @Input()
  showStatus = false;
  @Input()
  isExperts: boolean;
  @Input()
  buttonMode = false;
  @Input()
  modify = false;
  
  
  @ViewChild('searchBoxTemplate', { static: true })
  templateDialog: TemplateRef<any>;

  public allowDollar: boolean;
  public locale: string;
  public country: string;
  public brand: string;
  public channel: string;
  public textFind: string;
  public textAvailables: string;
  public textAvailable: string;
  public dates: any;
  public distribution: any;
  public hostnameArray: any;
  public isWeb: boolean;
  public countryMap: any;
  public resultTotals: string;
  public statusItems: { icon: string; text: string }[];
  public initialData: any;
  public disableARS: boolean;
  private HOTEL = 'HOTEL';
  private dialogRef: MbDialogRef<unknown>;

  constructor(
    private searchStateService: SearchStateService,
    private analyticsService: AnalyticsService,
    private reqService: ReqService,
    private i18nService: I18nService,
    private hotelsResultsService: HotelsResultsService,
    private dateService: DateService,
    private vcr: ViewContainerRef,
    private dialog: MbDialog,
    private readonly deviceService: DeviceService,
  ) {
    this.textFind = this.i18nService.translate('Encontramos');
    this.textAvailables = this.i18nService.translate('alojamientos disponibles');
    this.textAvailable = this.i18nService.translate('alojamiento disponible');
    this.dates = this.searchStateService.getDecodedDate();
    this.distribution = this.searchStateService.getDecodedRooms();
    this.hostnameArray = location.hostname.split('.');
    this.isWeb =
      this.hostnameArray[0] !== 'sucursales' && this.hostnameArray[0] !== 'ccr';
    this.countryMap = {
      ar: 'arg',
      mx: 'mex',
      co: 'col',
      br: 'bra',
    };
  }

  ngOnInit() {
    this.locale = this.reqService.getLocale();
    this.country = this.countryMap[this.hostnameArray[this.hostnameArray.length - 1]];
    this.brand = this.reqService.getBrand();
    this.channel = this.reqService.getChannel();
    this.allowDollar = this.reqService.allowDollar;
    this.disableARS = this.reqService.disableARS;

    if (!this.dates && this.isLanding) {
      const searchToday = this.dateService.getDatesToday();
      this.dates = {
        from: searchToday.from.toISOString().split('T')[0],
        to: searchToday.to.toISOString().split('T')[0],
      };
    }
    this.setStatusItems();
    this.setSearchboxFields();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.total && !changes.total.isFirstChange()) {
      const newTotal = [
        {
          icon: '',
          text:
            this.total > 1
              ? this.textFind + ' ' + this.total + ' ' + this.textAvailables
              : this.total === 1
                ? this.textFind + ' 1 ' + this.textAvailable
                : this.textFind + ' 0 ' + this.textAvailables,
        },
      ];
      this.resultTotals =
        this.total > 1
          ? this.total + ' ' + this.textAvailables
          : this.total === 1
            ? '1 ' + this.textAvailable
            : '0 ' + this.textAvailables;
      this.statusItems = newTotal.concat(this.statusItems.splice(1));
    }
    if (changes.searchedEntity && !changes.searchedEntity.isFirstChange()) {
      this.searchedEntity = changes.searchedEntity.currentValue;
      this.setSearchDescription();
    }
  }

  setSearchboxFields() {
    const rooms = this.distribution.rooms.map((room) => {
      return {
        adults: room.adults,
        children: room.children.length,
        childrenAges: room.children.map((child) => {
          return { value: child };
        }),
      };
    });

    this.initialData = {
      currencyCode: this.searchStateService.getCurrencyCode(),
      checkin: new Date(this.dates.from.replace(/-/g, '/')).toISOString(),
      checkout: new Date(this.dates.to.replace(/-/g, '/')).toISOString(),
      guests: { rooms: rooms, selectedRooms: rooms.length },
      origin: this.getInitialDataOrigin(),
    };
  }

  private getInitialDataOrigin() {
    if (this.searchedEntity) {
      return {
        label: this.searchedEntity.name,
        id: this.searchedEntity.id,
        group: this.searchedEntity.type && this.searchedEntity.type.toLowerCase(),
        code: null,
        iata: null,
      };
    } else {
      return {
        label: this.searchStateService.getEntityName(),
        id: this.searchStateService.getEntityCode(),
        group: this.searchStateService.getType(),
        code: null,
        iata: null,
      };
    }
  }

  public trackReSearchToggle(toggleReSearchForm) {
    this.analyticsService.trackEvent({
      section: this.section === 'detail' ? 'Detail' : 'Results',
      action: 'Toggle Research Form',
      label: toggleReSearchForm ? 'Show' : 'Hide',
    });
  }

  public openDialog() {
    this.showStatus = !this.showStatus;

    this.dialogRef = this.dialog.open(this.templateDialog, {
      closeOnEscKey: true,
      viewContainerRef: this.vcr,
    });

    this.trackReSearchToggle(this.showStatus);
  }

  public trackReSearchSubmit() {
    this.analyticsService.trackEvent({
      section: this.section === 'detail' ? 'Detail' : 'Results',
      action: 'Submit Research Form',
      label: 'Submit Form',
    });
  }

  /**
   * Takes search data, map to hotel model and process search
   * @param {Object} data
   */
  public async handleSearch(data) {
    let type = (data.origin.group || '').toUpperCase();
    let sluggedOrigin = this.slugOrigin(data.origin);

    const oldSearchId = this.searchedEntity ? this.searchedEntity.id : '';
    if (type === this.HOTEL && data.origin.id !== oldSearchId) {
      try {
        const res: IHotelEntityCity = await this.hotelsResultsService
          .getEntityCity(data.origin.id)
          .toPromise();
        sluggedOrigin = this.slugEntityCityResponse(res);
        type = res.entity_type;
      } catch (error) {
        console.error(error);
      }
    }

    const featuredHotelId =
      (data.origin.group || '').toUpperCase() === this.HOTEL &&
      this.searchedEntity &&
      data.origin.id !== this.searchedEntity.id
        ? data.origin.id
        : '';

    const normalizedData = {
      type: type,
      destination: sluggedOrigin,
      checkin: this.dateService.parseLiteralDate(data.checkin),
      checkout: this.dateService.parseLiteralDate(data.checkout),
      distribution: this.encodeSearchboxDistribution(data.guests),
      featured_hotel_id: featuredHotelId,
    };

    this.processSearch(normalizedData, data.currencyCode);
  }

  private processSearch(data, currencyCode) {
    this.trackReSearchSubmit();
    this.showStatus = false;
    const newSearchId = data.destination.split('-').pop();
    const oldSearchId = this.searchedEntity && this.searchedEntity.id;

    if (this.isLanding) {
      this.showStatus = true;

      window.open(this.makeUrl(data), '_self');
    } else if (
      (this.section === 'detail' || data.type === this.HOTEL) &&
      newSearchId !== oldSearchId
    ) {
      window.open(this.makeUrl(data), '_blank');
    } else {
      this.searchStateService.setSection(
        this.section === 'detail' ? 'detail' : 'results',
      );
      this.searchStateService.setType(data.type);
      this.searchStateService.setSlug(data.destination);
      this.searchStateService.setDate(`${data.checkin},${data.checkout}`);
      this.searchStateService.setRooms(data.distribution);
      this.searchStateService.setFilters('');
      this.searchStateService.setOffset('');
      this.searchStateService.setFlexible('');
      if (data.featured_hotel_id) {
        this.searchStateService.setFeaturedHotelId(data.featured_hotel_id);
      } else {
        if (newSearchId !== oldSearchId) {
          this.searchStateService.setFeaturedHotelId('');
        } else {
          this.searchStateService.setFeaturedHotelId(
            this.searchStateService.getFeaturedHotelId(),
          );
        }
      }
      if (currencyCode !== undefined) {
        this.searchStateService.setCurrencyCode(currencyCode);
      }
      this.setSearchDescription();
      this.resetState.emit(true);
      if (this.deviceService.isMobile()) {
        this.dialogRef.close();
      }
    }
  }

  private slugEntityCityResponse(res) {
    const sluggedEntityName = res.entity_name
      .split(',')[0]
      .replace(/\//g, '-')
      .toLowerCase()
      .split(' ')
      .join('-');

    return `${sluggedEntityName}-${res.entity_id}`;
  }

  private slugOrigin(origin) {
    const label = origin.label
      .split(',')[0]
      .replace(/<mark>/g, '')
      .replace(/<\/mark>/g, '')
      .replace(/\//g, '-')
      .toLowerCase()
      .split(' ')
      .join('-');

    return `${label}-${origin.id}`;
  }

  /**
   * Takes a distribution object and encodes it into its string form
   * @param {Object} distribution
   * @returns {string}
   */
  private encodeSearchboxDistribution(distribution) {
    return distribution.rooms
      .map((room) => {
        let children = '';
        if (room.childrenAges.length) {
          children =
            '-' +
            room.childrenAges
              .map(({ value }) => value)
              .join()
              .replace(/,/g, '-');
        }
        return room.adults + children;
      })
      .join('!');
  }

  private setSearchDescription() {
    this.dates = this.searchStateService.getDecodedDate();
    this.distribution = this.searchStateService.getDecodedRooms();
    this.setStatusItems();
  }

  public setStatusItems() {
    this.resultTotals =
      this.total > 1
        ? this.total + ' ' + this.textAvailables
        : this.total === 1
          ? '1 ' + this.textAvailable
          : '0 ' + this.textAvailables;
    this.statusItems = [
      {
        icon: null,
        text:
          this.total > 1
            ? this.textFind + ' ' + this.total + ' ' + this.textAvailables
            : this.total === 1
              ? this.textFind + ' 1 ' + this.textAvailable
              : this.textFind + ' 0 ' + this.textAvailables,
      },
      {
        icon: null,
        text: this.busy ? '...' : this.searchedEntity ? this.searchedEntity.name : null,
      },
      {
        icon: 'calendar',
        text: !this.isLanding ? this.setDate(this.dates) : '',
      },
      {
        icon: 'sleep',
        text: this.distribution.rooms.length,
      },
      {
        icon: 'login',
        text: this.distribution.total,
      },
    ];
  }

  /**
   * Takes all the search data from the controller and returns a valid hotel search url
   * @return {string}
   */
  private makeUrl(data) {
    const featured = data.featured_hotel_id
      ? '&featuredHotelId=' + data.featured_hotel_id
      : '';
    return (
      '/hotels' +
      (data.type === this.HOTEL || (this.isLanding && this.section === 'detail')
        ? '/detail/'
        : '/results/') +
      data.destination +
      '?date=' +
      data.checkin +
      ',' +
      data.checkout +
      '&rooms=' +
      data.distribution +
      '&type=' +
      data.type +
      featured
    );
  }

  private setDate(date) {
    const months = [];
    months.push(this.i18nService.translate('Ene'));
    months.push(this.i18nService.translate('Feb'));
    months.push(this.i18nService.translate('Mar'));
    months.push(this.i18nService.translate('Abr'));
    months.push(this.i18nService.translate('May'));
    months.push(this.i18nService.translate('Jun'));
    months.push(this.i18nService.translate('Jul'));
    months.push(this.i18nService.translate('Ago'));
    months.push(this.i18nService.translate('Sep'));
    months.push(this.i18nService.translate('Oct'));
    months.push(this.i18nService.translate('Nov'));
    months.push(this.i18nService.translate('Dic'));
    let from: any = new Date(date.from.split('-').join('/'));
    const fromDate = {
      day: from.getDate(),
      month: months[from.getMonth()],
      year: from.getFullYear(),
    };
    let to: any = new Date(date.to.split('-').join('/'));
    const toDate = {
      day: to.getDate(),
      month: months[to.getMonth()],
      year: to.getFullYear(),
    };
    from = fromDate.day + ' ';
    from += fromDate.month === toDate.month ? ' ' : ' ' + fromDate.month;
    from += fromDate.year === toDate.year ? ' ' : ' ' + fromDate.year;
    to = toDate.day + ' ' + toDate.month + ' ' + toDate.year;
    return from + ' al ' + to;
  }
}
